import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  DateField,
} from '@structural-climbing/ui'
import { useTrainers } from './useTrainers'

interface Props {
  trainerId: string
  trainerName: string
  onClose: () => void
  onDone: () => void
}

/**
 * A trainer is away, applied across their whole caseload.
 *
 * "Anton is off next week" is one event, but handling it per member meant one
 * trip through one profile per person — and any one forgotten left a member
 * quietly stranded.
 */
export function TrainerUnavailableModal({ trainerId, trainerName, onClose, onDone }: Props) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const { trainers } = useTrainers()

  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [mode, setMode] = useState<'extend' | 'reassign'>('extend')
  const [coverId, setCoverId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ changed: number; skipped: number; failed: number } | null>(
    null,
  )

  const others = trainers.filter((tr) => tr.user_id !== trainerId)
  const affected = trainers.find((tr) => tr.user_id === trainerId)?.assigned_count ?? 0

  async function handleSubmit() {
    if (!from || !to) return
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const resp = await fetch(`${apiUrl}/trainers/${encodeURIComponent(trainerId)}/unavailable`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from,
          to,
          mode,
          cover_user_id: mode === 'reassign' ? coverId : null,
        }),
      })
      const body = (await resp.json()) as {
        error?: string
        changed?: number
        skipped?: number
        failed?: number
      }
      if (!resp.ok) throw new Error(body.error ?? t('trainers.errorUnavailable'))
      setResult({
        changed: body.changed ?? 0,
        skipped: body.skipped ?? 0,
        failed: body.failed ?? 0,
      })
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('trainers.errorUnavailable'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal scrollBehavior="inside" isOpen onOpenChange={(open) => !open && onClose()}>
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex-col items-start gap-1">
              <span>{t('trainers.awayTitle')}</span>
              <span className="text-sm font-normal text-foreground/60">{trainerName}</span>
            </ModalHeader>
            <ModalBody>
              {result ? (
                <div className="flex flex-col gap-2 py-2">
                  <p className="text-sm text-success">
                    {t('trainers.awayDone', { count: result.changed })}
                  </p>
                  {result.skipped > 0 && (
                    <p className="text-xs text-foreground/50">
                      {t('trainers.awaySkipped', { count: result.skipped })}
                    </p>
                  )}
                  {result.failed > 0 && (
                    <p className="text-xs text-danger">
                      {t('trainers.awayFailed', { count: result.failed })}
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <p className="text-sm text-foreground/60">
                    {t('trainers.awayAffects', { count: affected })}
                  </p>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <DateField
                      label={t('trainers.awayFrom')}
                      value={from}
                      onValueChange={setFrom}
                    />
                    <DateField label={t('trainers.awayTo')} value={to} onValueChange={setTo} />
                  </div>

                  <Select
                    label={t('trainers.awayModeLabel')}
                    selectedKeys={[mode]}
                    onSelectionChange={(keys) =>
                      setMode((Array.from(keys)[0] as 'extend' | 'reassign') ?? 'extend')
                    }
                  >
                    <SelectItem key="extend" textValue={t('trainers.awayModeExtend')}>
                      {t('trainers.awayModeExtend')}
                    </SelectItem>
                    <SelectItem key="reassign" textValue={t('trainers.awayModeReassign')}>
                      {t('trainers.awayModeReassign')}
                    </SelectItem>
                  </Select>

                  {mode === 'reassign' &&
                    (others.length === 0 ? (
                      <p className="text-sm text-foreground/60">{t('trainers.noOtherTrainers')}</p>
                    ) : (
                      <Select
                        label={t('trainers.awayCoverLabel')}
                        selectedKeys={coverId ? [coverId] : []}
                        onSelectionChange={(keys) =>
                          setCoverId((Array.from(keys)[0] as string) ?? '')
                        }
                      >
                        {others.map((tr) => (
                          <SelectItem key={tr.user_id} textValue={tr.name}>
                            {tr.name}
                          </SelectItem>
                        ))}
                      </Select>
                    ))}

                  <p className="text-xs text-foreground/50">
                    {mode === 'extend'
                      ? t('trainers.awayExtendHint')
                      : t('trainers.awayReassignHint')}
                  </p>

                  {error && <p className="text-sm text-danger">{error}</p>}
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              {result ? (
                <Button color="primary" onClick={onClose}>
                  {t('common.done')}
                </Button>
              ) : (
                <>
                  <Button variant="flat" onClick={onClose} isDisabled={saving}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleSubmit}
                    isLoading={saving}
                    isDisabled={!from || !to || (mode === 'reassign' && !coverId)}
                  >
                    {t('trainers.awayApply')}
                  </Button>
                </>
              )}
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
